import * as Color from "color"
import PptxGenJS, { Slide } from "pptxgenjs"
import { Decomposition } from "../debt-table"
import * as DebtUtils from "../utils"
import { ReportConfiguration } from "../../../configuration"
import { MasterSlide } from "../../pptx-configuration/master-slides"
import {
  Placeholder,
  getPlaceholderChartBodyOptions
} from "../../pptx-configuration/placeholders"
import { DocumentSection } from "../../types"
import { formatTitle } from "../../utils"
import { LoanWithDecomposition, LocalAuthority, Ppi, Scenario } from "../../../../../model"

export function checkCapitalReimbursementSection(
  configuration: ReportConfiguration
): boolean {
  const capitalReimbursement = configuration.debt.capitalReimbursement
  return capitalReimbursement.globalCharts || capitalReimbursement.tables
}

export function createCapitalReimbursementSection(
  pptx: PptxGenJS,
  configuration: ReportConfiguration,
  numbering: number[],
  decomposedLoans: LoanWithDecomposition[],
  years: number[],
  localAuthority:LocalAuthority,
  loans: any,
  scenario: Scenario,
  ppi?:Ppi
): DocumentSection {
  const slides: Slide[] = []
  const slidesTitle = formatTitle("Remboursement capital", numbering)
  const capitalReimbursement = configuration.debt.capitalReimbursement
  const placeholderBody = getPlaceholderChartBodyOptions()
  const baseColor = new Color(configuration.table.header.backgroundColor)

  if (capitalReimbursement.tables) {
    DebtUtils.includeDebtTable(
      pptx,
      slidesTitle,
      "management-report-debt-capital-reimbursement",
      Decomposition.CapitalReimbursement,
      decomposedLoans,
      years,
      configuration
    )
  }

  if (capitalReimbursement.globalCharts) {
    const slide = pptx.addNewSlide(MasterSlide.Chart)

    slide.addText(slidesTitle, {
      placeholder: Placeholder.Title,
    })

    const chartData = DebtUtils.createChartData(
      Decomposition.CapitalReimbursement,
      decomposedLoans,
      years,
      localAuthority,
      loans,
      scenario,
      ppi
    )

    const chartOptions = DebtUtils.createGlobalChartOptions(baseColor)

    slide.addChart(pptx.charts.BAR, chartData, chartOptions)

    slides.push(slide)
  }

  if (capitalReimbursement.PieChartType) {
    const slide = pptx.addNewSlide(MasterSlide.Chart)

    slide.addText(slidesTitle, {
      placeholder: Placeholder.Title,
    })

    const pieChartData = DebtUtils.createPieChartData(
      decomposedLoans,
      "reimbursed",
      configuration.generalities.referenceYear
    )

    const pieChartOptions = DebtUtils.createPieChartOptions(
      pieChartData,
      placeholderBody,
      baseColor
    )

    slide.addChart(pptx.charts.PIE, pieChartData, pieChartOptions)
    slides.push(slide)
  }

  if (capitalReimbursement.perBank || capitalReimbursement.perRateType) {
    const slide = pptx.addNewSlide(MasterSlide.Chart)

    slide.addText(slidesTitle, {
      placeholder: Placeholder.Title,
    })

    if (capitalReimbursement.perBank) {
      const perBankChartData = DebtUtils.createPerBankChartData(
        decomposedLoans,
        "reimbursed",
        configuration.generalities.referenceYear
      )

      const perBankChartOptions = DebtUtils.createPerBankChartOptions(
        perBankChartData,
        capitalReimbursement.perRateType,
        placeholderBody,
        baseColor
      )

      slide.addChart(pptx.charts.PIE, perBankChartData, perBankChartOptions)
    }

    if (capitalReimbursement.perRateType) {
      const perRateTypeChartData = DebtUtils.createPerRateTypeChartData(
        decomposedLoans,
        "reimbursed",
        configuration.generalities.referenceYear
      )

      const perRateTypeChartOptions = DebtUtils.createPerRateTypeChartOptions(
        capitalReimbursement.perBank,
        placeholderBody,
        baseColor
      )

      slide.addChart(
        pptx.charts.PIE,
        perRateTypeChartData,
        perRateTypeChartOptions
      )
    }

    slides.push(slide)
  }

  return { slides, title: slidesTitle }
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/debt-generator/capital-reimbursement-generator/index.tsx